import React, { useState, useCallback, useRef, useMemo } from "react";
import api from "../api";

/* ════════════════════════════════════════════════════════════════
   ProductGridEditor — 4분면 그리드 레이아웃 + 드래그 드롭 + 멀티이미지 + 레이어
   ════════════════════════════════════════════════════════════════ */

const GRID_ZONES = {
  "1x1": { cols: 1, rows: 1, label: "1칸 전체" },
  "1x2": { cols: 1, rows: 2, label: "상하 2분할" },
  "2x1": { cols: 2, rows: 1, label: "좌우 2분할" },
  "2x2": { cols: 2, rows: 2, label: "4분면" },
  "3x1": { cols: 3, rows: 1, label: "3열" },
  "1x3": { cols: 1, rows: 3, label: "3행" },
};

/* ── 버튼 프리셋 ── */
const BUTTON_PRESETS = {
  purchase: { label: "바로구매", color: "bg-blue-600 text-white", action: "purchase" },
  phone: { label: "전화문의", color: "bg-gray-900 text-white", action: "phone" },
  kakao: { label: "카톡문의", color: "bg-yellow-400 text-gray-900", action: "kakao" },
  inquiry: { label: "문의하기", color: "bg-gray-700 text-white", action: "inquiry" },
  cart: { label: "장바구니", color: "bg-emerald-600 text-white", action: "cart" },
  compare: { label: "비교하기", color: "bg-gray-200 text-gray-800", action: "compare" },
  quote: { label: "견적요청", color: "bg-indigo-600 text-white", action: "quote" },
  download: { label: "카탈로그 다운로드", color: "bg-gray-100 text-gray-700", action: "download" },
};

/* ── 블록 타입 ── */
const BLOCK_TYPES = {
  image: { label: "이미지", icon: "🖼️" },
  title: { label: "제목", icon: "📝" },
  text: { label: "텍스트", icon: "📄" },
  rich_text: { label: "리치 텍스트", icon: "📰" },
  price: { label: "가격", icon: "💰" },
  options: { label: "옵션 선택", icon: "⚙️" },
  buttons: { label: "버튼 그룹", icon: "🔘" },
  specs: { label: "스펙 미니", icon: "📋" },
  badges: { label: "뱃지/태그", icon: "🏷️" },
  rating: { label: "평점", icon: "⭐" },
  spacer: { label: "여백", icon: "↕️" },
};

/* ═══════════════ 이미지 업로드 헬퍼 (그리드 블록용) ═══════════════ */
function GridImageUploader({ src, onUrlChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const upload = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await api.post("/api/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUrlChange(res.data.url);
    } catch (err) {
      alert("이미지 업로드 실패");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex items-center gap-1">
      <input value={src || ""} onChange={e => onUrlChange(e.target.value)}
        placeholder="URL 또는 파일 업로드" className="flex-1 rounded border px-2 py-1 text-xs" />
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={e => upload(e.target.files?.[0])} />
      <button onClick={() => inputRef.current?.click()} disabled={uploading}
        className="text-[10px] px-1.5 py-1 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 whitespace-nowrap">
        {uploading ? "⏳" : "📎"}
      </button>
    </div>
  );
}

/* ═══════════════ 리치 텍스트 에디터 (Word-like) ═══════════════ */
function RichTextEditor({ block, onChange, onRemove }) {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const execCmd = (cmd, val) => {
    document.execCommand(cmd, false, val);
    syncContent();
  };

  const syncContent = () => {
    if (editorRef.current) {
      onChange({ ...block, html: editorRef.current.innerHTML });
    }
  };

  const uploadInlineImage = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await api.post("/api/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      document.execCommand("insertImage", false, res.data.url);
      syncContent();
    } catch (err) {
      alert("이미지 업로드 실패");
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        uploadInlineImage(item.getAsFile());
        return;
      }
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-500">📰 리치 텍스트</span>
        <button onClick={onRemove} className="text-red-400 text-xs">✕</button>
      </div>
      {/* Toolbar */}
      <div className="flex gap-0.5 flex-wrap bg-gray-50 rounded-lg p-1 border border-gray-200">
        <button onClick={() => execCmd("bold")} className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 font-bold">B</button>
        <button onClick={() => execCmd("italic")} className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 italic">I</button>
        <button onClick={() => execCmd("underline")} className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 underline">U</button>
        <span className="w-px bg-gray-300 mx-0.5" />
        <button onClick={() => execCmd("formatBlock", "H2")} className="px-1.5 py-0.5 text-[10px] rounded hover:bg-gray-200 font-bold">H2</button>
        <button onClick={() => execCmd("formatBlock", "H3")} className="px-1.5 py-0.5 text-[10px] rounded hover:bg-gray-200 font-bold">H3</button>
        <button onClick={() => execCmd("formatBlock", "P")} className="px-1.5 py-0.5 text-[10px] rounded hover:bg-gray-200">P</button>
        <span className="w-px bg-gray-300 mx-0.5" />
        <button onClick={() => execCmd("insertUnorderedList")} className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200">•</button>
        <button onClick={() => execCmd("insertOrderedList")} className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200">1.</button>
        <span className="w-px bg-gray-300 mx-0.5" />
        <button onClick={() => execCmd("justifyLeft")} className="px-1.5 py-0.5 text-[10px] rounded hover:bg-gray-200">◧</button>
        <button onClick={() => execCmd("justifyCenter")} className="px-1.5 py-0.5 text-[10px] rounded hover:bg-gray-200">◫</button>
        <button onClick={() => execCmd("justifyRight")} className="px-1.5 py-0.5 text-[10px] rounded hover:bg-gray-200">◨</button>
        <span className="w-px bg-gray-300 mx-0.5" />
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200">
          {uploading ? "⏳" : "🖼️"}
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
          onChange={e => uploadInlineImage(e.target.files?.[0])} />
      </div>
      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={syncContent}
        onPaste={handlePaste}
        dangerouslySetInnerHTML={{ __html: block.html || "" }}
        className="min-h-[80px] border border-gray-200 rounded-lg p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white prose prose-sm max-w-none
          [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-2"
        style={{ whiteSpace: "pre-wrap" }}
      />
      <p className="text-[10px] text-gray-400">이미지: 붙여넣기(Ctrl+V) 또는 🖼️ 버튼으로 추가</p>
    </div>
  );
}

/* ═══════════════ 그리드 블록 하나의 에디터 ═══════════════ */
function BlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const upd = (patch) => onChange({ ...block, ...patch });

  switch (block.type) {
    case "image":
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500">🖼️ 이미지 (레이어 {block.zIndex || 0})</span>
            <div className="flex gap-1">
              {!isFirst && <button onClick={onMoveUp} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded">↑</button>}
              {!isLast && <button onClick={onMoveDown} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded">↓</button>}
              <button onClick={onRemove} className="text-red-400 text-xs">✕</button>
            </div>
          </div>
          {/* 멀티이미지 */}
          <div className="space-y-1">
            {(block.images || [block.src ? { src: block.src, alt: "" } : null]).filter(Boolean).map((img, ii) => (
              <div key={ii} className="flex items-center gap-1">
                <div className="flex-1">
                  <GridImageUploader src={img.src} onUrlChange={url => {
                    const imgs = [...(block.images || [{ src: block.src, alt: "" }])];
                    imgs[ii] = { ...imgs[ii], src: url };
                    upd({ images: imgs });
                  }} />
                </div>
                <button onClick={() => {
                  const imgs = (block.images || []).filter((_, k) => k !== ii);
                  upd({ images: imgs });
                }} className="text-red-400 text-[10px]">✕</button>
              </div>
            ))}
            <button onClick={() => upd({ images: [...(block.images || []), { src: "", alt: "" }] })}
              className="text-[10px] text-blue-600">+ 이미지 추가</button>
          </div>
          <div className="grid grid-cols-3 gap-1 text-[10px]">
            <div>
              <label className="text-gray-400">레이어 z</label>
              <input type="number" value={block.zIndex || 0} onChange={e => upd({ zIndex: parseInt(e.target.value) || 0 })}
                className="w-full rounded border px-1 py-0.5" />
            </div>
            <div>
              <label className="text-gray-400">너비 %</label>
              <input type="number" value={block.widthPct || 100} onChange={e => upd({ widthPct: parseInt(e.target.value) || 100 })}
                className="w-full rounded border px-1 py-0.5" min={10} max={100} />
            </div>
            <div>
              <label className="text-gray-400">정렬</label>
              <select value={block.align || "center"} onChange={e => upd({ align: e.target.value })}
                className="w-full rounded border px-1 py-0.5">
                <option value="left">좌</option>
                <option value="center">중앙</option>
                <option value="right">우</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-[10px] text-gray-500">
            <input type="checkbox" checked={!!block.bgRemoved} onChange={e => upd({ bgRemoved: e.target.checked })} />
            배경 제거됨 (투명)
          </label>
        </div>
      );

    case "options":
      return (
        <OptionCategoryEditor
          categories={block.categories || []}
          onChange={cats => upd({ categories: cats })}
          onRemove={onRemove}
        />
      );

    case "buttons":
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500">🔘 버튼 그룹</span>
            <button onClick={onRemove} className="text-red-400 text-xs">✕</button>
          </div>
          <select value={block.layout || "stack"} onChange={e => upd({ layout: e.target.value })}
            className="w-full rounded border px-2 py-1 text-xs">
            <option value="stack">세로 배치</option>
            <option value="row">가로 배치</option>
            <option value="grid2">2열 그리드</option>
          </select>
          {(block.buttons || []).map((btn, bi) => (
            <div key={bi} className="flex items-center gap-1 bg-gray-50 rounded p-1.5">
              <input value={btn.label || ""} onChange={e => {
                const btns = [...(block.buttons || [])]; btns[bi] = { ...btns[bi], label: e.target.value };
                upd({ buttons: btns });
              }} className="flex-1 rounded border px-2 py-0.5 text-xs" placeholder="버튼 텍스트" />
              <select value={btn.action || "inquiry"} onChange={e => {
                const btns = [...(block.buttons || [])]; btns[bi] = { ...btns[bi], action: e.target.value };
                upd({ buttons: btns });
              }} className="rounded border px-1 py-0.5 text-[10px]">
                {Object.entries(BUTTON_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <button onClick={() => upd({ buttons: (block.buttons || []).filter((_, k) => k !== bi) })}
                className="text-red-400 text-[10px]">✕</button>
            </div>
          ))}
          <div className="flex gap-1 flex-wrap">
            {Object.entries(BUTTON_PRESETS).slice(0, 4).map(([key, preset]) => (
              <button key={key} onClick={() => upd({ buttons: [...(block.buttons || []), { label: preset.label, action: key, color: preset.color }] })}
                className="text-[10px] px-2 py-0.5 rounded-full border text-blue-600 hover:bg-blue-50">
                + {preset.label}
              </button>
            ))}
          </div>
        </div>
      );

    case "price":
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500">💰 가격</span>
            <button onClick={onRemove} className="text-red-400 text-xs">✕</button>
          </div>
          <input value={block.priceLabel || ""} onChange={e => upd({ priceLabel: e.target.value })}
            placeholder="라벨 (예: 월 렌탈료)" className="w-full rounded border px-2 py-1 text-xs" />
          <input type="number" value={block.amount || 0} onChange={e => upd({ amount: parseInt(e.target.value) || 0 })}
            placeholder="금액" className="w-full rounded border px-2 py-1 text-xs" />
          <select value={block.priceStyle || "large"} onChange={e => upd({ priceStyle: e.target.value })}
            className="w-full rounded border px-2 py-1 text-xs">
            <option value="large">크게</option>
            <option value="medium">중간</option>
            <option value="small">작게</option>
          </select>
        </div>
      );

    case "title":
    case "text":
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500">{block.type === "title" ? "📝 제목" : "📄 텍스트"}</span>
            <button onClick={onRemove} className="text-red-400 text-xs">✕</button>
          </div>
          {block.type === "title" ? (
            <input value={block.content || ""} onChange={e => upd({ content: e.target.value })}
              placeholder="제목 텍스트" className="w-full rounded border px-2 py-1 text-sm font-bold" />
          ) : (
            <textarea value={block.content || ""} onChange={e => upd({ content: e.target.value })}
              placeholder="본문 텍스트 (여러 줄 가능)" className="w-full rounded border px-2 py-1 text-xs" rows={6}
              style={{ minHeight: "80px", resize: "vertical" }} />
          )}
          <select value={block.align || "left"} onChange={e => upd({ align: e.target.value })}
            className="w-full rounded border px-1 py-0.5 text-[10px]">
            <option value="left">좌측</option><option value="center">중앙</option><option value="right">우측</option>
          </select>
        </div>
      );

    case "rich_text":
      return <RichTextEditor block={block} onChange={updated => onChange(updated)} onRemove={onRemove} />;

    default:
      return (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{BLOCK_TYPES[block.type]?.icon} {BLOCK_TYPES[block.type]?.label || block.type}</span>
          <button onClick={onRemove} className="text-red-400 text-xs">✕</button>
        </div>
      );
  }
}

/* ═══════════════ 옵션 카테고리 에디터 (대제목 > 옵션들) ═══════════════ */
function OptionCategoryEditor({ categories, onChange, onRemove }) {
  const addCategory = () => onChange([...categories, { title: "", options: [{ name: "", price: 0 }] }]);
  const updCat = (ci, patch) => { const arr = [...categories]; arr[ci] = { ...arr[ci], ...patch }; onChange(arr); };
  const updOpt = (ci, oi, patch) => {
    const arr = [...categories];
    const opts = [...arr[ci].options]; opts[oi] = { ...opts[oi], ...patch };
    arr[ci] = { ...arr[ci], options: opts }; onChange(arr);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-500">⚙️ 옵션 (카테고리별)</span>
        <button onClick={onRemove} className="text-red-400 text-xs">✕</button>
      </div>
      {categories.map((cat, ci) => (
        <div key={ci} className="border border-indigo-200 rounded-lg p-3 bg-indigo-50/30 space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-indigo-600 text-white text-[10px] font-bold w-5 h-5 rounded flex items-center justify-center">{ci + 1}</span>
            <input value={cat.title || ""} onChange={e => updCat(ci, { title: e.target.value })}
              placeholder="대제목 (예: 계약기간, 세단기추가)" className="flex-1 rounded border px-2 py-1 text-sm font-bold" />
            <select value={cat.displayType || "dropdown"} onChange={e => updCat(ci, { displayType: e.target.value })}
              className="rounded border px-1 py-0.5 text-[10px]">
              <option value="dropdown">드롭다운</option>
              <option value="button">버튼형</option>
              <option value="card">카드형</option>
              <option value="radio">라디오</option>
            </select>
            <button onClick={() => onChange(categories.filter((_, k) => k !== ci))} className="text-red-400 text-xs">✕</button>
          </div>
          {(cat.options || []).map((opt, oi) => (
            <div key={oi} className="flex gap-2 items-center bg-white rounded p-1.5">
              <input value={opt.name || ""} onChange={e => updOpt(ci, oi, { name: e.target.value })}
                placeholder={`옵션 ${oi + 1}`} className="flex-1 rounded border px-2 py-0.5 text-xs" />
              <input type="number" value={opt.price || 0} onChange={e => updOpt(ci, oi, { price: parseInt(e.target.value) || 0 })}
                placeholder="가격" className="w-20 rounded border px-2 py-0.5 text-xs" />
              <span className="text-[10px] text-gray-400">원</span>
              <button onClick={() => updCat(ci, { options: cat.options.filter((_, k) => k !== oi) })} className="text-red-300 text-[10px]">✕</button>
            </div>
          ))}
          <button onClick={() => updCat(ci, { options: [...(cat.options || []), { name: "", price: 0 }] })}
            className="text-[10px] text-indigo-600">+ 옵션 추가</button>
        </div>
      ))}
      <button onClick={addCategory} className="text-xs text-indigo-600 font-medium hover:underline">+ 카테고리 추가</button>
      <div className="flex gap-1 flex-wrap">
        {["계약기간", "세단기추가", "색상", "용지함", "후처리기"].map(p => (
          <button key={p} onClick={() => onChange([...categories, { title: p, displayType: "dropdown", options: [{ name: "선택", price: 0 }] }])}
            className="text-[10px] px-2 py-0.5 rounded-full border border-indigo-300 text-indigo-600 hover:bg-indigo-100">+ {p}</button>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════ 그리드 셀 (드래그 드롭 대상) ═══════════════ */
function GridCell({ cellIndex, blocks, onBlocksChange, onAddBlock, gridLayout }) {
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const handleDragStart = (e, blockIdx) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ cellIndex, blockIdx }));
    setDragIdx(blockIdx);
  };
  const handleDragEnd = () => setDragIdx(null);
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (data.cellIndex === cellIndex) {
        // Reorder within cell
        const items = [...blocks];
        const [moved] = items.splice(data.blockIdx, 1);
        items.push(moved);
        onBlocksChange(items);
      }
      // Cross-cell drops handled by parent
    } catch {}
  };

  const moveBlock = (idx, dir) => {
    const items = [...blocks];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    onBlocksChange(items);
  };

  return (
    <div className={`border-2 border-dashed rounded-xl p-3 min-h-[120px] transition-colors ${dragOver ? "border-blue-400 bg-blue-50" : "border-gray-200 bg-gray-50/50"}`}
      onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      {blocks.length === 0 && (
        <div className="text-center text-gray-300 text-xs py-8">블록을 추가하세요</div>
      )}
      {blocks.map((block, bi) => (
        <div key={bi} draggable onDragStart={e => handleDragStart(e, bi)} onDragEnd={handleDragEnd}
          className={`mb-2 bg-white rounded-lg p-2 border border-gray-150 cursor-grab active:cursor-grabbing transition-opacity ${dragIdx === bi ? "opacity-30" : ""}`}>
          <BlockEditor block={block}
            onChange={updated => { const arr = [...blocks]; arr[bi] = updated; onBlocksChange(arr); }}
            onRemove={() => onBlocksChange(blocks.filter((_, k) => k !== bi))}
            onMoveUp={() => moveBlock(bi, -1)}
            onMoveDown={() => moveBlock(bi, 1)}
            isFirst={bi === 0} isLast={bi === blocks.length - 1} />
        </div>
      ))}
      {/* Add block buttons */}
      <div className="flex gap-1 flex-wrap mt-2">
        {Object.entries(BLOCK_TYPES).map(([type, { label, icon }]) => (
          <button key={type} onClick={() => onAddBlock(type)}
            className="text-[10px] px-1.5 py-0.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition">
            {icon}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════ 이미지 미리보기 (별도 컴포넌트 - hooks 규칙) ═══════════════ */
function PreviewImageBlock({ block }) {
  const imgs = block.images || (block.src ? [{ src: block.src }] : []);
  const [activeImg, setActiveImg] = useState(0);
  if (imgs.length === 0) return <div className="bg-gray-100 aspect-square rounded-xl flex items-center justify-center text-gray-300 text-sm">이미지 없음</div>;
  return (
    <div style={{ zIndex: block.zIndex || 0, width: `${block.widthPct || 100}%`, margin: block.align === "center" ? "0 auto" : block.align === "right" ? "0 0 0 auto" : undefined }}>
      <img src={imgs[activeImg]?.src} alt="" className="w-full object-contain rounded-xl" onError={e => { e.target.style.display = "none"; }} />
      {imgs.length > 1 && (
        <div className="flex gap-1 mt-2 justify-center">
          {imgs.map((img, ii) => (
            <button key={ii} onClick={() => setActiveImg(ii)}
              className={`w-10 h-10 rounded-lg overflow-hidden border-2 transition ${activeImg === ii ? "border-blue-500" : "border-gray-200"}`}>
              <img src={img.src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════ 그리드 미리보기 블록 렌더 ═══════════════ */
function PreviewBlock({ block }) {
  switch (block.type) {
    case "image":
      return <PreviewImageBlock block={block} />;
    case "title":
      return <h2 className={`text-xl font-bold text-gray-900 ${block.align === "center" ? "text-center" : block.align === "right" ? "text-right" : ""}`}>{block.content || "제목"}</h2>;
    case "text":
      return <p className={`text-sm text-gray-600 leading-relaxed whitespace-pre-wrap ${block.align === "center" ? "text-center" : block.align === "right" ? "text-right" : ""}`}>{block.content || "텍스트"}</p>;
    case "rich_text":
      return <div className="prose prose-sm max-w-none [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg" dangerouslySetInnerHTML={{ __html: block.html || "<p>리치 텍스트</p>" }} />;
    case "price":
      return (
        <div className={block.priceStyle === "large" ? "text-center" : ""}>
          {block.priceLabel && <span className="text-xs text-gray-400">{block.priceLabel}</span>}
          <p className={`font-bold ${block.priceStyle === "large" ? "text-2xl" : block.priceStyle === "medium" ? "text-lg" : "text-base"}`}>
            {(block.amount || 0).toLocaleString()}원
          </p>
        </div>
      );
    case "options":
      return <OptionCategoryPreview categories={block.categories || []} />;
    case "buttons":
      return (
        <div className={block.layout === "row" ? "flex gap-2" : block.layout === "grid2" ? "grid grid-cols-2 gap-2" : "space-y-2"}>
          {(block.buttons || []).map((btn, bi) => (
            <button key={bi} className={`px-4 py-3 rounded-xl text-sm font-bold w-full transition ${BUTTON_PRESETS[btn.action]?.color || "bg-gray-200 text-gray-800"}`}>
              {btn.label || "버튼"}
            </button>
          ))}
        </div>
      );
    case "badges":
      return <div className="flex gap-2 flex-wrap">{(block.items || ["새상품", "무료배송"]).map((b, i) => <span key={i} className="px-3 py-1 rounded-full bg-gray-100 text-xs font-medium text-gray-600">{b}</span>)}</div>;
    case "rating":
      return <div className="flex items-center gap-1"><span className="text-yellow-400">★</span><span className="text-sm font-bold">{block.score || "4.5"}</span><span className="text-xs text-gray-400">후기 {block.count || 0}건</span></div>;
    case "spacer":
      return <div style={{ height: block.height || 24 }} />;
    case "specs":
      return (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {(block.items || [{ label: "상품 구분", value: "새상품" }]).map((s, si) => (
            <div key={si} className="flex justify-between py-1 border-b border-gray-100">
              <span className="text-gray-400">{s.label}</span>
              <span className="font-bold">{s.value}</span>
            </div>
          ))}
        </div>
      );
    default:
      return <div className="text-xs text-gray-400">[{block.type}]</div>;
  }
}

/* ═══════════════ 옵션 카테고리 미리보기 (상세페이지 노출) ═══════════════ */
function OptionCategoryPreview({ categories }) {
  const [selected, setSelected] = useState({});

  const totalExtra = useMemo(() => {
    let sum = 0;
    categories.forEach((cat, ci) => {
      const selIdx = selected[ci];
      if (selIdx != null && cat.options[selIdx]) sum += cat.options[selIdx].price || 0;
    });
    return sum;
  }, [categories, selected]);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-bold text-gray-600">추가 옵션</h3>
      {categories.map((cat, ci) => (
        <div key={ci}>
          <label className="text-xs font-bold text-blue-600 mb-1 block">{cat.title}</label>
          {(cat.displayType === "dropdown" || !cat.displayType) && (
            <select value={selected[ci] ?? ""} onChange={e => setSelected(p => ({ ...p, [ci]: e.target.value === "" ? null : parseInt(e.target.value) }))}
              className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">선택</option>
              {(cat.options || []).map((opt, oi) => (
                <option key={oi} value={oi}>
                  {opt.name}{opt.price ? ` (+${opt.price.toLocaleString()}원)` : ""}
                </option>
              ))}
            </select>
          )}
          {cat.displayType === "button" && (
            <div className="flex flex-wrap gap-2">
              {(cat.options || []).map((opt, oi) => (
                <button key={oi} onClick={() => setSelected(p => ({ ...p, [ci]: p[ci] === oi ? null : oi }))}
                  className={`px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition ${selected[ci] === oi ? "border-blue-600 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-600"}`}>
                  {opt.name}
                  {opt.price > 0 && <span className="text-[10px] ml-1 opacity-60">+{opt.price.toLocaleString()}</span>}
                </button>
              ))}
            </div>
          )}
          {cat.displayType === "card" && (
            <div className="space-y-1">
              {(cat.options || []).map((opt, oi) => (
                <div key={oi} onClick={() => setSelected(p => ({ ...p, [ci]: p[ci] === oi ? null : oi }))}
                  className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition ${selected[ci] === oi ? "border-blue-600 bg-blue-50" : "border-gray-200"}`}>
                  <span className="text-sm font-medium">{opt.name}</span>
                  {opt.price > 0 && <span className="text-sm font-bold">+{opt.price.toLocaleString()}원</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {totalExtra > 0 && (
        <div className="text-right text-sm text-gray-500">옵션 추가금: <span className="font-bold text-gray-900">+{totalExtra.toLocaleString()}원</span></div>
      )}
    </div>
  );
}

/* ═══════════════ 메인 그리드 에디터 (섹션 타입) ═══════════════ */
export default function ProductGridEditor({ section, onChange }) {
  const gridLayout = section.gridLayout || "2x1";
  const { cols, rows } = GRID_ZONES[gridLayout] || GRID_ZONES["2x1"];
  const totalCells = cols * rows;

  // 셀 데이터: section.cells = [ { blocks: [...] }, ... ]
  const cells = section.cells || Array.from({ length: totalCells }, () => ({ blocks: [] }));

  const updateCells = (newCells) => onChange({ ...section, cells: newCells });
  const setGridLayout = (layout) => {
    const { cols: nc, rows: nr } = GRID_ZONES[layout];
    const tc = nc * nr;
    const newCells = Array.from({ length: tc }, (_, i) => cells[i] || { blocks: [] });
    onChange({ ...section, gridLayout: layout, cells: newCells });
  };

  const addBlock = (cellIdx, type) => {
    const newCells = [...cells];
    const defaultBlock = type === "buttons"
      ? { type, buttons: [{ label: "바로구매", action: "purchase", color: BUTTON_PRESETS.purchase.color }], layout: "stack" }
      : type === "options"
      ? { type, categories: [] }
      : type === "image"
      ? { type, images: [], zIndex: 0, widthPct: 100, align: "center" }
      : type === "rich_text"
      ? { type, html: "" }
      : { type, content: "", align: "left" };
    newCells[cellIdx] = { ...newCells[cellIdx], blocks: [...(newCells[cellIdx].blocks || []), defaultBlock] };
    updateCells(newCells);
  };

  const snapToGrid = () => {
    /* 자석 비율 맞추기: 모든 셀의 블록 수가 같도록 spacer 삽입 */
    const maxBlocks = Math.max(...cells.map(c => (c.blocks || []).length), 1);
    const snapped = cells.map(c => {
      const bl = c.blocks || [];
      if (bl.length < maxBlocks) {
        return { ...c, blocks: [...bl, ...Array.from({ length: maxBlocks - bl.length }, () => ({ type: "spacer", height: 24 }))] };
      }
      return c;
    });
    updateCells(snapped);
  };

  return (
    <div className="space-y-4">
      {/* Grid layout selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold text-gray-500">그리드 레이아웃:</span>
        {Object.entries(GRID_ZONES).map(([key, { label }]) => (
          <button key={key} onClick={() => setGridLayout(key)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${gridLayout === key ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            {label}
          </button>
        ))}
        <button onClick={snapToGrid} className="px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 ml-auto">
          🧲 자석 정렬
        </button>
      </div>

      {/* Grid editor */}
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, auto)` }}>
        {cells.slice(0, totalCells).map((cell, ci) => (
          <GridCell key={ci} cellIndex={ci} blocks={cell.blocks || []}
            onBlocksChange={newBlocks => { const nc = [...cells]; nc[ci] = { ...nc[ci], blocks: newBlocks }; updateCells(nc); }}
            onAddBlock={type => addBlock(ci, type)}
            gridLayout={gridLayout} />
        ))}
      </div>

      {/* Live Preview */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-bold text-gray-500">미리보기 (고객 화면)</span>
          <span className="text-[10px] text-gray-400">= 실제 상세페이지</span>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="grid gap-0" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, auto)` }}>
            {cells.slice(0, totalCells).map((cell, ci) => (
              <div key={ci} className="p-4 border border-gray-50">
                {(cell.blocks || []).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)).map((block, bi) => (
                  <div key={bi} className="mb-3">
                    <PreviewBlock block={block} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export { BUTTON_PRESETS, BLOCK_TYPES, GRID_ZONES, OptionCategoryPreview };
